/**
 * Timelog Page
 * ============
 * Time tracking page for customer billing.
 * Quick entry form, monthly report generator, and entry list.
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Clock,
  Plus,
  Pencil,
  Trash2,
  FileText,
  Users,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  Save,
} from 'lucide-react'
import Button from '@/components/Button'
import { timelogApi } from '@/services/api'
import {
  useTimelogCustomers,
  useCreateTimelogCustomer,
  useUpdateTimelogCustomer,
  useDeleteTimelogCustomer,
} from '@/hooks/useTimelog'
import { WORK_TYPE_LABELS } from '@/types'
import type {
  WorkType,
  TimelogCustomer,
  TimelogEntry,
  TimelogEntryCreate,
  TimelogEntryUpdate,
  PaginatedResponse,
} from '@/types'

// =============================================================================
// Helpers
// =============================================================================

function todayString(): string {
  return new Date().toISOString().split('T')[0]
}

function formatDateDisplay(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('is-IS', { day: 'numeric', month: 'short', year: 'numeric' })
}

const MONTH_OPTIONS = [
  { value: 1, label: 'Janúar' },
  { value: 2, label: 'Febrúar' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Apríl' },
  { value: 5, label: 'Maí' },
  { value: 6, label: 'Júní' },
  { value: 7, label: 'Júlí' },
  { value: 8, label: 'Ágúst' },
  { value: 9, label: 'September' },
  { value: 10, label: 'Október' },
  { value: 11, label: 'Nóvember' },
  { value: 12, label: 'Desember' },
]

// =============================================================================
// Customer Management Modal
// =============================================================================

function CustomerModal({
  customers,
  onClose,
}: {
  customers: TimelogCustomer[]
  onClose: () => void
}) {
  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')

  const createCustomer = useCreateTimelogCustomer()
  const updateCustomer = useUpdateTimelogCustomer()
  const deleteCustomer = useDeleteTimelogCustomer()

  const handleCreate = async () => {
    if (!newName.trim()) return
    await createCustomer.mutateAsync({ name: newName.trim() })
    setNewName('')
  }

  const handleUpdate = async (id: number) => {
    if (!editName.trim()) return
    await updateCustomer.mutateAsync({ id, data: { name: editName.trim() } })
    setEditingId(null)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Deactivate this customer? Existing entries will be preserved.')) return
    await deleteCustomer.mutateAsync(id)
  }

  const startEdit = (customer: TimelogCustomer) => {
    setEditingId(customer.id)
    setEditName(customer.name)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Manage Customers
          </h3>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
          >
            <X size={20} />
          </button>
        </div>

        {/* Add new customer */}
        <div className="mb-4 flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            placeholder="New customer name..."
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
          <Button
            onClick={handleCreate}
            disabled={!newName.trim() || createCustomer.isPending}
            size="sm"
          >
            <Plus size={16} />
          </Button>
        </div>

        {/* Customer list */}
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {customers.map((c) => (
            <div
              key={c.id}
              className={`flex items-center justify-between rounded px-3 py-2 ${
                c.is_active
                  ? 'bg-gray-50 dark:bg-gray-700/50'
                  : 'bg-gray-100 opacity-60 dark:bg-gray-700/30'
              }`}
            >
              {editingId === c.id ? (
                <div className="flex flex-1 items-center gap-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleUpdate(c.id)
                      if (e.key === 'Escape') setEditingId(null)
                    }}
                    className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    autoFocus
                  />
                  <button
                    onClick={() => handleUpdate(c.id)}
                    className="text-green-600 hover:text-green-700"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <span className={`text-sm ${c.is_active ? 'text-gray-900 dark:text-white' : 'text-gray-500 line-through dark:text-gray-400'}`}>
                    {c.name}
                    {!c.is_active && <span className="ml-2 text-xs text-gray-400">(inactive)</span>}
                  </span>
                  {c.is_active && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => startEdit(c)}
                        className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-600 dark:hover:text-gray-300"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(c.id)}
                        className="rounded p-1 text-gray-400 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-end">
          <Button variant="secondary" onClick={onClose} size="sm">
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

export default function TimelogPage() {
  // Form state
  const [formDate, setFormDate] = useState(todayString())
  const [formCustomerId, setFormCustomerId] = useState<number | ''>('')
  const [formWorkType, setFormWorkType] = useState<WorkType>('remote')
  const [formDescription, setFormDescription] = useState('')
  const [formHours, setFormHours] = useState('')

  // List state
  const [page, setPage] = useState(1)
  const limit = 20

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editDate, setEditDate] = useState('')
  const [editCustomerId, setEditCustomerId] = useState<number>(0)
  const [editWorkType, setEditWorkType] = useState<WorkType>('remote')
  const [editDescription, setEditDescription] = useState('')
  const [editHours, setEditHours] = useState('')

  // Report state
  const now = new Date()
  const [reportYear, setReportYear] = useState(now.getFullYear())
  const [reportMonth, setReportMonth] = useState(now.getMonth() + 1)
  const [reportCustomerId, setReportCustomerId] = useState<number | ''>('')

  // Modal state
  const [showCustomerModal, setShowCustomerModal] = useState(false)

  // Highlighted entry for pulse animation after create
  const [highlightedEntryId, setHighlightedEntryId] = useState<number | null>(null)

  useEffect(() => {
    if (highlightedEntryId) {
      const timer = setTimeout(() => setHighlightedEntryId(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [highlightedEntryId])

  // Data hooks — customers still via React Query
  const { data: customersData } = useTimelogCustomers()

  // Entry mutations — native fetch only (React Query mutateAsync was throwing silently,
  // preventing fetchEntries() from ever running after create/update/delete)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Entries — plain React state, NO React Query (bypasses all caching layers)
  const [entriesData, setEntriesData] = useState<PaginatedResponse<TimelogEntry> | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchEntries = useCallback(async () => {
    setIsLoading(true)
    try {
      // Native fetch with cache: 'no-store' — strongest browser cache bypass.
      // Previous attempts with axios + React Query all returned stale data.
      const params = new URLSearchParams({ page: String(page), limit: String(limit) })
      const res = await fetch(`/api/timelog/entries?${params}`, {
        cache: 'no-store',
        credentials: 'include',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data: PaginatedResponse<TimelogEntry> = await res.json()
      setEntriesData(data)
      return data
    } catch (err) {
      console.error('Failed to fetch timelog entries:', err)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [page, limit])

  useEffect(() => {
    fetchEntries()
  }, [fetchEntries])

  const customers = customersData?.data ?? []
  const activeCustomers = customers.filter((c) => c.is_active)
  const entries = entriesData?.data ?? []
  const meta = entriesData?.meta

  // Year range for report picker
  const yearOptions: number[] = []
  for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) {
    yearOptions.push(y)
  }

  // Form submission — native fetch + re-fetch list
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formCustomerId || !formHours || !formDate) return

    setIsSubmitting(true)
    try {
      const payload: TimelogEntryCreate = {
        date: formDate,
        customer_id: formCustomerId as number,
        work_type: formWorkType,
        description: formDescription.trim() || null,
        hours: parseFloat(formHours),
      }

      const createRes = await fetch('/api/timelog/entries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      })
      if (!createRes.ok) throw new Error(`Create failed: HTTP ${createRes.status}`)

      const created = await createRes.json()

      // Re-fetch the entry list
      await fetchEntries()

      // Highlight the newly created entry (pulse animation)
      if (created?.data?.id) {
        setHighlightedEntryId(created.data.id)
      }

      // Reset form (keep date and work type)
      setFormCustomerId('')
      setFormDescription('')
      setFormHours('')
    } catch (err) {
      console.error('Failed to create timelog entry:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Edit handlers
  const startEdit = (entry: typeof entries[0]) => {
    setEditingId(entry.id)
    setEditDate(entry.date)
    setEditCustomerId(entry.customer_id)
    setEditWorkType(entry.work_type)
    setEditDescription(entry.description || '')
    setEditHours(String(entry.hours))
  }

  const cancelEdit = () => setEditingId(null)

  const saveEdit = async () => {
    if (!editingId) return
    setIsSaving(true)
    try {
      const payload: TimelogEntryUpdate = {
        date: editDate,
        customer_id: editCustomerId,
        work_type: editWorkType,
        description: editDescription.trim() || null,
        hours: parseFloat(editHours),
      }

      const updateRes = await fetch(`/api/timelog/entries/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      })
      if (!updateRes.ok) throw new Error(`Update failed: HTTP ${updateRes.status}`)

      await fetchEntries()
      setEditingId(null)
    } catch (err) {
      console.error('Failed to update timelog entry:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this entry?')) return
    try {
      const deleteRes = await fetch(`/api/timelog/entries/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!deleteRes.ok) throw new Error(`Delete failed: HTTP ${deleteRes.status}`)
      await fetchEntries()
    } catch (err) {
      console.error('Failed to delete timelog entry:', err)
    }
  }

  // PDF report
  const handleGenerateReport = () => {
    const url = timelogApi.getReportUrl(reportYear, reportMonth, reportCustomerId || undefined)
    window.open(url, '_blank')
  }

  // Select styles for reuse
  const selectClass =
    'rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
  const inputClass =
    'rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white'

  return (
    <div className="space-y-6">
      {/* Pulse animation for newly added entries */}
      <style>{`
        @keyframes timelog-row-pulse {
          0%, 100% { background-color: transparent; }
          50% { background-color: rgba(59, 130, 246, 0.15); }
        }
        .timelog-row-pulse {
          animation: timelog-row-pulse 0.8s ease-in-out 3;
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Clock size={28} className="text-blue-600 dark:text-blue-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tímaskýrsla</h1>
        </div>
        <Button
          icon={<Users size={16} />}
          onClick={() => setShowCustomerModal(true)}
        >
          Manage Customers
        </Button>
      </div>

      {/* ── Quick Entry Form ── */}
      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Log Hours
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-wrap items-end gap-3">
            {/* Date */}
            <div className="w-40">
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                Date
              </label>
              <input
                type="date"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                className={`w-full ${inputClass}`}
                required
              />
            </div>

            {/* Customer */}
            <div className="w-48">
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                Customer
              </label>
              <select
                value={formCustomerId}
                onChange={(e) => setFormCustomerId(e.target.value ? Number(e.target.value) : '')}
                className={`w-full ${selectClass}`}
                required
              >
                <option value="">Select...</option>
                {activeCustomers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Work Type */}
            <div className="w-36">
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                Type
              </label>
              <select
                value={formWorkType}
                onChange={(e) => setFormWorkType(e.target.value as WorkType)}
                className={`w-full ${selectClass}`}
              >
                <option value="remote">Fjarvinna</option>
                <option value="on_site">Á staðnum</option>
              </select>
            </div>

            {/* Hours */}
            <div className="w-20">
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                Hours
              </label>
              <input
                type="number"
                step="0.25"
                min="0.25"
                max="24"
                value={formHours}
                onChange={(e) => setFormHours(e.target.value)}
                placeholder="1.5"
                className={`w-full ${inputClass}`}
                required
              />
            </div>

            {/* Description */}
            <div className="min-w-[200px] flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <input
                type="text"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="What did you do?"
                className={`w-full ${inputClass}`}
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={!formCustomerId || !formHours || isSubmitting}
            >
              <Plus size={16} className="mr-1.5" />
              {isSubmitting ? 'Saving...' : 'Log'}
            </Button>
          </div>
        </form>
      </div>

      {/* ── Monthly Report ── */}
      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Monthly Report
        </h2>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Year
            </label>
            <select
              value={reportYear}
              onChange={(e) => setReportYear(Number(e.target.value))}
              className={selectClass}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Month
            </label>
            <select
              value={reportMonth}
              onChange={(e) => setReportMonth(Number(e.target.value))}
              className={selectClass}
            >
              {MONTH_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Customer
            </label>
            <select
              value={reportCustomerId}
              onChange={(e) => setReportCustomerId(e.target.value ? Number(e.target.value) : '')}
              className={selectClass}
            >
              <option value="">All Customers</option>
              {activeCustomers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <Button onClick={handleGenerateReport} variant="secondary">
            <FileText size={16} className="mr-1.5" />
            Generate PDF
          </Button>
        </div>
      </div>

      {/* ── Entry List ── */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Recent Entries
            </h2>
            {meta && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {meta.total} total
              </span>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            Loading...
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Clock size={40} className="mb-3" />
            <p>No time entries yet. Log your first hours above.</p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3 text-right">Hours</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {entries.map((entry) => (
                    <tr
                      key={entry.id}
                      className={
                        entry.id === highlightedEntryId
                          ? 'timelog-row-pulse'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                      }
                    >
                      {editingId === entry.id ? (
                        /* Edit mode */
                        <>
                          <td className="px-4 py-2">
                            <input
                              type="date"
                              value={editDate}
                              onChange={(e) => setEditDate(e.target.value)}
                              className={`w-32 ${inputClass} py-1 text-xs`}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <select
                              value={editCustomerId}
                              onChange={(e) => setEditCustomerId(Number(e.target.value))}
                              className={`w-32 ${selectClass} py-1 text-xs`}
                            >
                              {activeCustomers.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <select
                              value={editWorkType}
                              onChange={(e) => setEditWorkType(e.target.value as WorkType)}
                              className={`w-28 ${selectClass} py-1 text-xs`}
                            >
                              <option value="remote">Fjarvinna</option>
                              <option value="on_site">Á staðnum</option>
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              className={`w-full ${inputClass} py-1 text-xs`}
                            />
                          </td>
                          <td className="px-4 py-2 text-right">
                            <input
                              type="number"
                              step="0.25"
                              min="0.25"
                              max="24"
                              value={editHours}
                              onChange={(e) => setEditHours(e.target.value)}
                              className={`w-20 text-right ${inputClass} py-1 text-xs`}
                            />
                          </td>
                          <td className="px-4 py-2 text-right">
                            <div className="flex justify-end gap-1">
                              <button
                                onClick={saveEdit}
                                disabled={isSaving}
                                className="rounded p-1.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30"
                                title="Save"
                              >
                                <Save size={14} />
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="rounded p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                                title="Cancel"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        /* View mode */
                        <>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {formatDateDisplay(entry.date)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                            {entry.customer_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            <span
                              className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                entry.work_type === 'remote'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                  : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                              }`}
                            >
                              {WORK_TYPE_LABELS[entry.work_type] || entry.work_type}
                            </span>
                          </td>
                          <td className="max-w-xs truncate px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            {entry.description || '—'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-gray-900 dark:text-white">
                            {entry.hours}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              <button
                                onClick={() => startEdit(entry)}
                                className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={() => handleDelete(entry.id)}
                                className="rounded p-1.5 text-gray-400 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {meta && meta.total_pages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 px-5 py-3 dark:border-gray-700">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Page {meta.page} of {meta.total_pages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={!meta.has_prev}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={!meta.has_next}
                  >
                    Next
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Customer Management Modal */}
      {showCustomerModal && (
        <CustomerModal
          customers={customers}
          onClose={() => setShowCustomerModal(false)}
        />
      )}
    </div>
  )
}
